import { useParams, Link } from "react-router-dom";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";
import Breadcrumb from "@/components/base/Breadcrumb";
import ArticleCard from "@/components/base/ArticleCard";
import SEOHead from "@/components/base/SEOHead";
import SchemaOrg from "@/components/base/SchemaOrg";
import NotFound from "@/pages/NotFound";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return <NotFound />;
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(",")}
        canonicalUrl={`https://akeelah-guide.com/article/${slug}`}
        ogType="article"
        ogImage={article.image}
        articlePublishedTime={article.publishDate}
        articleModifiedTime={article.publishDate}
      />
      <SchemaOrg type="article" articleSlug={slug} />

      <main className="pt-20">
        <article>
          <div className="w-full px-4 md:px-6 py-8 bg-background-100 border-b border-background-200/70">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                { label: category?.name || article.category, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>

          <div className="w-full px-4 md:px-6 py-8">
            <div className="max-w-3xl mx-auto">
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  <Link
                    to={`/category/${article.category}`}
                    className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium cursor-pointer"
                  >
                    {category?.name || article.category}
                  </Link>
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 bg-secondary-100 text-secondary-900 rounded-full text-xs font-medium">
                      {tag}
                    </span>
                  ))}
                </div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 leading-tight" style={{ fontFamily: "var(--font-heading)" }}>
                  {article.title}
                </h1>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full overflow-hidden bg-background-200">
                    <img src={article.authorAvatar} alt={article.author} className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-700">{article.author}</p>
                    <p className="text-xs text-foreground-500">{article.publishDate} · 阅读约{article.readTime}</p>
                  </div>
                </div>
              </div>

              <div className="mb-8 rounded-lg overflow-hidden">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-auto object-cover rounded-lg"
                />
              </div>

              <div
                className="prose max-w-none text-foreground-700 leading-relaxed article-content"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-12 pt-8 border-t border-background-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200">
                      <img src={article.authorAvatar} alt={article.author} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-700">{article.author}</p>
                      <p className="text-xs text-foreground-500">资深影评人</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-12 bg-background-100 border-t border-background-200/70">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-xl font-bold text-foreground-950 mb-6" style={{ fontFamily: "var(--font-heading)" }}>
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.slug} article={ra} variant="default" />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  );
}